import { Component } from '@opten/gin-tonic/helpers/compiler';

type DotNetRef = { invokeMethodAsync(method: string, ...args: unknown[]): Promise<unknown> };

declare global {
	interface Window {
		SlideshowComponent?: typeof SlideshowComponent;
		slideshowInit?: (urls?: string[] | string, dotNetRef?: DotNetRef) => void;
		slideshowPause?: (paused: boolean) => void;
		slideshowDestroy?: () => void;
	}
}

const CONTAINER_SELECTOR = 'js-slideshow';
const SCROLLER_SELECTOR = '.c-slideshow__carousel-container';

// one pass over all photos takes this long (the speed of the former CSS animation)
const LOOP_DURATION_MS = 60000;
// after the last manual scroll the photos start moving again
const RESUME_AFTER_MS = 10000;
// a mouse drag further than this is a scroll, not a click on a photo
const DRAG_THRESHOLD_PX = 5;

// one slideshow per scroll container: a second init on the same element replaces the first
const instances = new Map<HTMLElement, SlideshowComponent>();

/**
 * Photo wall: the photos as honeycomb rows that scroll by themselves when they don't fit the screen
 * (an endless loop over two copies of the honeycomb). Scrolling by hand (wheel, touch, mouse drag)
 * takes over and pauses the automatic scrolling until RESUME_AFTER_MS without interaction.
 * A click on a photo calls back into Blazor (OpenPhoto) or, without a callback, opens the lightbox.
 */
@Component({ className: CONTAINER_SELECTOR })
export class SlideshowComponent {
	public static readonly CONTAINER_SELECTOR = `.${CONTAINER_SELECTOR}`;

	private readonly scroller: HTMLElement;
	private readonly track: HTMLElement;
	private readonly honeycomb1: HTMLElement;
	private readonly honeycomb2: HTMLElement;
	private readonly lightbox: HTMLElement | null;

	private images: string[] = [];
	private imagesKey = '';
	private dotNetRef?: DotNetRef;

	private looping = false;
	private pausedByUser = false;
	private pausedExternally = false;
	private position = 0;
	private lastFrame = 0;
	private frameId = 0;
	private resumeTimer = 0;
	private drag: { x: number; scroll: number; moved: boolean } | null = null;
	private suppressClick = false;

	private readonly onResize = () => this.render();
	private readonly onInteraction = () => this.pauseForUser();
	private readonly onWheel = (e: WheelEvent) => this.wheel(e);
	private readonly onScroll = () => this.scrolled();
	private readonly onPointerDown = (e: PointerEvent) => this.pointerDown(e);
	private readonly onPointerMove = (e: PointerEvent) => this.pointerMove(e);
	private readonly onPointerUp = () => this.pointerUp();
	private readonly onClick = (e: MouseEvent) => this.click(e);
	private readonly onLightboxClick = () => this.closeLightbox();
	private readonly onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			this.closeLightbox();
		}
	};
	private readonly onFrame = (time: number) => this.frame(time);

	/**
	 * Accepts the photos (array or JSON string) or a root element (the slideshow or its scroll
	 * container; the photos then come from the container's data-images attribute).
	 */
	constructor(imagesOrContainer?: string[] | string | HTMLElement, dotNetRef?: DotNetRef) {
		const root = imagesOrContainer instanceof HTMLElement ? imagesOrContainer : document;
		const scroller =
			imagesOrContainer instanceof HTMLElement && imagesOrContainer.matches(SCROLLER_SELECTOR)
				? imagesOrContainer
				: root.querySelector<HTMLElement>(SCROLLER_SELECTOR);
		if (!scroller) {
			throw new Error('Slideshow: no .c-slideshow__carousel-container');
		}

		this.scroller = scroller;
		this.track = scroller.querySelector('#carousel-track') as HTMLElement;
		this.honeycomb1 = scroller.querySelector('#honeycomb1') as HTMLElement;
		this.honeycomb2 = scroller.querySelector('#honeycomb2') as HTMLElement;
		this.lightbox =
			(scroller.closest('.c-slideshow')?.querySelector('#lightbox') as HTMLElement | null) ??
			document.getElementById('lightbox');

		instances.get(scroller)?.destroy();
		instances.set(scroller, this);

		this.scroller.addEventListener('wheel', this.onWheel, { passive: false });
		this.scroller.addEventListener('touchstart', this.onInteraction, { passive: true });
		this.scroller.addEventListener('touchmove', this.onInteraction, { passive: true });
		this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
		this.scroller.addEventListener('pointerdown', this.onPointerDown);
		this.scroller.addEventListener('click', this.onClick);
		window.addEventListener('pointermove', this.onPointerMove);
		window.addEventListener('pointerup', this.onPointerUp);
		window.addEventListener('resize', this.onResize);
		this.lightbox?.addEventListener('click', this.onLightboxClick);
		document.addEventListener('keydown', this.onKeyDown);

		let images: string[] = [];
		if (typeof imagesOrContainer === 'string') {
			images = JSON.parse(imagesOrContainer);
		} else if (Array.isArray(imagesOrContainer)) {
			images = imagesOrContainer;
		} else {
			const data = this.scroller.getAttribute('data-images');
			images = data ? JSON.parse(data) : [];
		}

		this.update(images, dotNetRef);
		this.frameId = requestAnimationFrame(this.onFrame);
	}

	public static instanceFor(scroller: HTMLElement): SlideshowComponent | undefined {
		return instances.get(scroller);
	}

	/** New photos (e.g. the periodic refresh): rebuilds only when they changed, keeps the position. */
	public update(images: string[], dotNetRef?: DotNetRef): void {
		this.dotNetRef = dotNetRef ?? this.dotNetRef;

		const key = JSON.stringify(images ?? []);
		if (key === this.imagesKey) {
			return;
		}

		this.imagesKey = key;
		this.images = images ?? [];
		this.render();
	}

	/** Holds the automatic scrolling, e.g. while a photo is open. */
	public setPaused(paused: boolean): void {
		this.pausedExternally = paused;
		this.lastFrame = 0;
	}

	public destroy(): void {
		cancelAnimationFrame(this.frameId);
		window.clearTimeout(this.resumeTimer);
		this.scroller.removeEventListener('wheel', this.onWheel);
		this.scroller.removeEventListener('touchstart', this.onInteraction);
		this.scroller.removeEventListener('touchmove', this.onInteraction);
		this.scroller.removeEventListener('scroll', this.onScroll);
		this.scroller.removeEventListener('pointerdown', this.onPointerDown);
		this.scroller.removeEventListener('click', this.onClick);
		window.removeEventListener('pointermove', this.onPointerMove);
		window.removeEventListener('pointerup', this.onPointerUp);
		window.removeEventListener('resize', this.onResize);
		this.lightbox?.removeEventListener('click', this.onLightboxClick);
		document.removeEventListener('keydown', this.onKeyDown);

		if (instances.get(this.scroller) === this) {
			instances.delete(this.scroller);
		}
	}

	public get isConnected(): boolean {
		return this.scroller.isConnected;
	}

	private render(): void {
		this.createHoneycomb(this.honeycomb1, this.images);
		this.initializeCarousel();
	}

	private getRowCount(): number {
		let hexHeight = 300; // one hex height
		let hexOverlap = 50; // vertical overlap between rows
		let headerHeight = 180; // height of any fixed header/title
		const footerHeight = 100; // height of any fixed footer (if needed)
		const viewportHeight = window.innerHeight;

		// Adjust values depending on screen size
		if (this.getBreakpoint() === 'sm') {
			hexHeight = 150;
			hexOverlap = 25;
			headerHeight = 130;
		}

		// remaining height for honeycomb
		const availableHeight = viewportHeight - headerHeight - footerHeight - hexOverlap;

		// compute how many rows fit into availableHeight
		return Math.max(1, Math.floor((availableHeight + hexOverlap) / (hexHeight - hexOverlap)));
	}

	private createHoneycomb(container: HTMLElement, images: string[]): void {
		container.innerHTML = '';

		let rowCount = this.getRowCount();
		const imagesLength = images.length;

		if (imagesLength < 2) {
			rowCount = 1;
		} else if (imagesLength < 6 && rowCount > 2) {
			rowCount = 2;
		}

		const imagesPerRow = imagesLength > 0 ? Math.ceil(imagesLength / rowCount) : 0;
		let imageIndex = 0;

		for (let r = 0; r < rowCount; r++) {
			const row = document.createElement('div');
			row.classList.add('c-slideshow__row');
			if (r % 2 !== 0) {
				row.classList.add('even');
			}

			for (let c = 0; c < imagesPerRow && imageIndex < imagesLength; c++) {
				const hex = document.createElement('div');
				hex.classList.add('hex');

				const img = document.createElement('img');
				img.src = images[imageIndex];
				img.draggable = false;

				hex.appendChild(img);
				row.appendChild(hex);

				imageIndex++;
			}

			container.appendChild(row);

			if (imageIndex >= imagesLength) {
				break;
			}
		}
	}

	// Wider than the screen: a second copy of the honeycomb makes the loop seamless and the
	// photos scroll; otherwise they stand centered.
	private initializeCarousel(): void {
		if (this.honeycomb1.scrollWidth > this.scroller.clientWidth) {
			this.honeycomb2.style.display = 'flex';
			this.createHoneycomb(this.honeycomb2, this.images);
			this.track.classList.remove('center');
			this.looping = true;
			this.position = this.wrap(this.scroller.scrollLeft);
			this.scroller.scrollLeft = this.position;
		} else {
			this.honeycomb2.style.display = 'none';
			this.honeycomb2.innerHTML = '';
			this.track.classList.add('center');
			this.looping = false;
			this.scroller.scrollLeft = 0;
		}

		this.lastFrame = 0;
	}

	// distance after which the second copy looks exactly like the start
	private get period(): number {
		return this.honeycomb2.offsetLeft - this.honeycomb1.offsetLeft;
	}

	private wrap(position: number): number {
		const period = this.period;
		if (!this.looping || period <= 0) {
			return position;
		}

		return ((position % period) + period) % period;
	}

	private frame(time: number): void {
		if (this.looping && !this.pausedByUser && !this.pausedExternally && !document.hidden) {
			// capped, so a frame after a background tab doesn't jump
			const elapsed = this.lastFrame ? Math.min(100, time - this.lastFrame) : 0;
			this.position = this.wrap(this.position + (elapsed * this.period) / LOOP_DURATION_MS);
			this.scroller.scrollLeft = this.position;
		}

		this.lastFrame = time;
		this.frameId = requestAnimationFrame(this.onFrame);
	}

	private pauseForUser(): void {
		this.pausedByUser = true;
		window.clearTimeout(this.resumeTimer);
		this.resumeTimer = window.setTimeout(() => {
			this.pausedByUser = false;
			this.position = this.scroller.scrollLeft;
			this.lastFrame = 0;
		}, RESUME_AFTER_MS);
	}

	// Scrolling by hand (incl. touch momentum) keeps the pause alive and loops endlessly
	// in both directions; the automatic scrolling's own updates are ignored here.
	private scrolled(): void {
		if (!this.pausedByUser) {
			return;
		}

		this.pauseForUser();

		const raw = this.scroller.scrollLeft;
		const period = this.period;
		let target = raw;
		if (this.looping && period > 0) {
			// past the first copy: back to the same picture at the start; at the very
			// start: on to the same picture in the second copy, so scrolling left goes on too
			if (raw >= period) {
				target = raw - period;
			} else if (raw <= 0) {
				target = period - 1;
			}
		}

		if (target !== raw) {
			this.scroller.scrollLeft = target;
			if (this.drag) {
				this.drag.scroll += target - raw;
			}
		}

		this.position = this.scroller.scrollLeft;
	}

	// a plain (vertical) mouse wheel scrolls the photos sideways
	private wheel(e: WheelEvent): void {
		this.pauseForUser();
		if (this.looping && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
			e.preventDefault();
			this.scroller.scrollLeft += e.deltaY;
		}
	}

	// mouse: drag to scroll (touch scrolls natively)
	private pointerDown(e: PointerEvent): void {
		this.pauseForUser();
		if (e.pointerType === 'mouse' && e.button === 0) {
			this.drag = { x: e.clientX, scroll: this.scroller.scrollLeft, moved: false };
			this.position = this.scroller.scrollLeft;
		}
	}

	private pointerMove(e: PointerEvent): void {
		if (!this.drag) {
			return;
		}

		const dx = e.clientX - this.drag.x;
		if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
			this.drag.moved = true;
			this.scroller.classList.add('is-dragging');
		}

		if (this.drag.moved) {
			this.scroller.scrollLeft = this.drag.scroll - dx;
		}
	}

	private pointerUp(): void {
		if (this.drag?.moved) {
			// the click that ends a drag must not open a photo
			this.suppressClick = true;
		}

		this.drag = null;
		this.scroller.classList.remove('is-dragging');
	}

	private click(e: MouseEvent): void {
		if (this.suppressClick) {
			this.suppressClick = false;
			return;
		}

		const img = (e.target as HTMLElement).closest('.hex')?.querySelector('img');
		if (img) {
			this.openPhoto(img.src);
		}
	}

	private openPhoto(src: string): void {
		if (this.dotNetRef) {
			this.dotNetRef.invokeMethodAsync('OpenPhoto', src).catch(() => undefined);
			return;
		}

		if (this.lightbox) {
			const img = this.lightbox.querySelector('img') as HTMLImageElement;
			img.src = src;
			this.lightbox.style.display = 'flex';
			this.setPaused(true);
		}
	}

	private closeLightbox(): void {
		if (this.lightbox && this.lightbox.style.display === 'flex') {
			this.lightbox.style.display = 'none';
			this.setPaused(false);
		}
	}

	private getBreakpoint(): string {
		if (window.matchMedia('(max-width: 640px)').matches) {
			return 'sm';
		} else if (window.matchMedia('(max-width: 1024px)').matches) {
			return 'md';
		} else if (window.matchMedia('(max-width: 1440px)').matches) {
			return 'lg';
		}

		return 'xl';
	}
}

// Blazor (Pages/Events/Public.razor, Pages/Library/ControlPublic.razor): called after every render.
// Re-uses the slideshow while its container stays on the page (only new photos are applied),
// starts a new one when Blazor rendered a new container.
let current: SlideshowComponent | undefined;

window.SlideshowComponent = SlideshowComponent;
window.slideshowInit = (urls?: string[] | string, dotNetRef?: DotNetRef) => {
	const scroller = document.querySelector<HTMLElement>(SCROLLER_SELECTOR);
	if (!scroller) {
		return;
	}

	const images: string[] = typeof urls === 'string' ? JSON.parse(urls) : urls ?? [];
	const existing = SlideshowComponent.instanceFor(scroller);
	if (existing) {
		current = existing;
		existing.update(images, dotNetRef);
		return;
	}

	if (current && !current.isConnected) {
		current.destroy();
	}

	current = new SlideshowComponent(images, dotNetRef);
};
window.slideshowPause = (paused: boolean) => current?.setPaused(paused);
window.slideshowDestroy = () => {
	current?.destroy();
	current = undefined;
};
